import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import GetAppIcon from '@mui/icons-material/GetApp';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosBanner, setShowIosBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already dismissed this session
    if (sessionStorage.getItem('install-dismissed')) {
      setDismissed(true);
      return;
    }

    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Android/desktop Chrome: capture install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari: show manual instructions
    const ua = navigator.userAgent;
    const isIos = /iphone|ipad|ipod/i.test(ua);
    const isSafari = /safari/i.test(ua) && !/crios|fxios|chrome/i.test(ua);
    if (isIos && isSafari) {
      setShowIosBanner(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setDeferredPrompt(null);
    setShowIosBanner(false);
    sessionStorage.setItem('install-dismissed', '1');
  };

  if (dismissed) return null;

  // Android/Chrome install prompt
  if (deferredPrompt) {
    return (
      <Paper
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          zIndex: 1300,
        }}
      >
        <GetAppIcon sx={{ color: "primary.main", fontSize: 24 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight="bold">Install Seriously</Typography>
          <Typography variant="caption" color="text.secondary">
            Add to your home screen for quick access
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          onClick={handleInstall}
          sx={{ flexShrink: 0 }}
        >
          Install
        </Button>
        <IconButton size="small" onClick={handleDismiss}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    );
  }

  // iOS Safari manual install instructions
  if (showIosBanner) {
    return (
      <Paper
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          zIndex: 1300,
        }}
      >
        <IosShareIcon sx={{ color: "primary.main", fontSize: 24 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight="bold">Install Seriously</Typography>
          <Typography variant="caption" color="text.secondary">
            Tap the share button, then "Add to Home Screen"
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleDismiss}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    );
  }

  return null;
};

export default InstallPrompt;
